import axios from "axios";
import { defineStore } from "pinia";
import { useAppDataStore } from "@/stores/app-data";

const client = axios.create({ baseURL: import.meta.env.VITE_API_BASE_URL });

export type LocationSummary = Pick<
  PMC.Parking.Location,
  | "locationId"
  | "name"
  | "timezone"
  | "supportedParkingOperations"
  | "parkingSpaces"
>;

// Dashboard permissions that should fall back to the `partnerPortal:*` wildcard
// when the specific key is absent. Mirrors the server-side canAccessLocation
// wildcard behavior so UI isn't empty for wildcard-only users after we stopped
// expanding `partnerPortal:*` into per-dashboard keys on the server.
export const WILDCARD_FALLBACK_PREFIX = "partnerPortal:use";
export const WILDCARD_PERMISSION = "partnerPortal:*";

/**
 * Entries are location IDs, except for venue/event permissions which prepend a
 * `venue:<id>` marker string before the location IDs. Use getLocationsForPermission
 * (below) to resolve entries safely — it filters out the venue marker.
 */
export interface UserPermissionMap {
  [key: string]: string[];
}

export interface UserGroup {
  groupId: string;
  name: string;
  locationIds: string[];
}

// permission -> the surfaced group ids that permission actually grants (from the
// server). Absent on sessions persisted before this field existed — that `undefined`
// is meaningful: callers treat it as "fall back to the legacy accessible-group
// behavior" until the next token refresh provides the map. A present map (even {})
// means server-scoped.
export type PermissionGroupsMap = Record<string, string[]>;

export interface AppUserStoreUser {
  attributes: {
    id: string;
    name: string;
    email: string;
  };
  permissions: UserPermissionMap;
  locations: Record<string, LocationSummary>;
  groups: UserGroup[];
  // Which of `groups` each dashboard permission grants. Optional so a session
  // persisted before this existed hydrates without it (see PermissionGroupsMap).
  permissionGroups?: PermissionGroupsMap;
}

export interface AppUserStoreState {
  user: AppUserStoreUser | null;
  accessToken: string;
  refreshToken: string;
  [k: string]: any;
}

const isLocationId = (value: unknown): value is string =>
  typeof value === "string" && !!value && !value.includes(":");

export const useAppUserStore = defineStore({
  id: "appUser",
  state: (): AppUserStoreState => {
    return {
      user: null,
      accessToken: "",
      refreshToken: "",
    };
  },
  actions: {
    setUserAndTokens(
      user: AppUserStoreUser | null,
      accessToken: string,
      refreshToken: string,
    ) {
      this.user = user;
      this.accessToken = accessToken;
      this.refreshToken = refreshToken;
    },
    setPermissionsAndLocations(
      permissions: UserPermissionMap,
      locations: Record<string, LocationSummary>,
    ) {
      if (!this.user) {
        throw new Error("User not set");
      }

      // Assign together so consumers never see IDs that don't yet resolve.
      this.user.locations = locations || {};
      this.user.permissions = permissions || {};
    },
    postStartAuthentication(email: string) {
      return client.post("/v1/auth/startAuthentication", { email });
    },
    async postRequestToken(email: string, otp: string) {
      const res = await client.post("/v1/auth/requestToken", { email, otp });

      const {
        user,
        accessToken,
        refreshToken,
        permissions,
        locations,
        groups,
        permissionGroups,
      } = res.data.data;

      this.setUserAndTokens(
        {
          attributes: user,
          permissions: permissions || {},
          locations: locations || {},
          groups: groups || [],
          // Preserve undefined when the backend omits it (an OLD backend during the
          // deploy window) so dashboardGroupsFor's legacy fallback stays active — {}
          // would wrongly read as "scoped: no groups" and blank the group selector.
          permissionGroups: permissionGroups ?? undefined,
        },
        accessToken,
        refreshToken,
      );
    },
    async postRefreshToken() {
      if (!this.refreshToken) {
        return Promise.reject(new Error("No refresh token available"));
      }

      const response = await client
        .post("/v1/auth/refreshToken", {
          refreshToken: this.refreshToken,
        })
        .catch(() => null);

      const payload = response?.data?.data as
        | {
            accessToken?: string;
            permissions?: UserPermissionMap;
            locations?: Record<string, LocationSummary>;
            groups?: UserGroup[] | null;
            permissionGroups?: PermissionGroupsMap | null;
          }
        | undefined;

      if (!payload?.accessToken) {
        return Promise.reject(new Error("Failed to refresh token"));
      }

      const { accessToken, permissions, locations, groups, permissionGroups } =
        payload;

      // Refresh group scope BEFORE the actions below — the persist plugin hooks
      // setUserAndTokens/setPermissionsAndLocations, so groups must be updated
      // first to be written to storage. Skip on null so a transient resolve
      // failure doesn't clobber the loaded list. A group's membership can change
      // between loads.
      if (this.user && groups) {
        this.user.groups = groups;
        // Move in lockstep with groups, but preserve undefined when an OLD backend
        // omits it (deploy window): that keeps the legacy fallback active with the
        // fresh group list instead of forcing scoped-empty ({}) and blanking the
        // selector. A new backend always sends the map (or {} for "no groups").
        this.user.permissionGroups = permissionGroups ?? undefined;
      }

      this.setUserAndTokens(this.user, accessToken, this.refreshToken);

      // Update permissions and locations together if either was returned.
      if (this.user && (permissions || locations)) {
        this.setPermissionsAndLocations(
          permissions || this.user.permissions,
          locations || this.user.locations,
        );
      }

      return Promise.resolve(accessToken);
    },
    signOut() {
      const appDataStore = useAppDataStore();

      this.$reset();

      // Also reset any other stores here on sign out action.
      appDataStore.$reset();
    },
  },
  getters: {
    name(): string {
      return this.user?.attributes?.name || "";
    },
    isAuthenticated(): boolean {
      return !!this.accessToken && !!this.refreshToken;
    },
    permissions(): UserPermissionMap {
      return this.user?.permissions || {};
    },
    locations(): Record<string, LocationSummary> {
      return this.user?.locations || {};
    },
    groups(): UserGroup[] {
      return this.user?.groups || [];
    },
    permissionGroups(): PermissionGroupsMap | undefined {
      // Undefined (not {}) is intentional — it signals a pre-permissionGroups session
      // so callers can fall back to legacy behavior. A present map means server-scoped.
      return this.user?.permissionGroups;
    },
    getLocation(): (id: string) => LocationSummary | undefined {
      return (id: string) => this.user?.locations?.[id];
    },
    /**
     * Resolves a permission key to the LocationSummary objects the user has access to.
     * - Filters out the `venue:<id>` marker string (only a display flag, not a location).
     * - For `partnerPortal:use*` keys, falls back to `partnerPortal:*` entries when the
     *   specific key is absent or empty. This mirrors server-side canAccessLocation
     *   wildcard semantics; server no longer pre-expands the wildcard into per-dashboard keys.
     */
    getLocationsForPermission(): (permission: string) => LocationSummary[] {
      return (permission: string) => {
        const permissions = this.user?.permissions || {};
        const locations = this.user?.locations || {};

        let ids = permissions[permission];

        // Fall back to partnerPortal:* only when the specific dashboard key is
        // truly absent/empty. Don't fall back for venue-only arrays — those are
        // intentionally scoped (event permissions) and must not pick up wildcard
        // locations.
        if (
          (!ids || ids.length === 0) &&
          permission.startsWith(WILDCARD_FALLBACK_PREFIX) &&
          permissions[WILDCARD_PERMISSION]
        ) {
          ids = permissions[WILDCARD_PERMISSION];
        }

        if (!ids) return [];

        const result: LocationSummary[] = [];
        for (const id of ids) {
          if (!isLocationId(id)) continue; // skip venue:<id> markers
          const loc = locations[id];
          if (loc) result.push(loc);
        }
        return result;
      };
    },
    /**
     * Event dashboard is scoped to specific venues via a `venue:<id>` marker
     * in `partnerPortal:useEventDashboard`. It is NOT granted by the
     * `partnerPortal:*` wildcard — wildcard users carry org location resources,
     * not venue resources, and `app-eventsStore.fetchEvents` needs the venueId
     * to function. Sidebar / route-guard must use this getter instead of
     * `canPerformAction` for Event, otherwise wildcard users land on a broken
     * Event page.
     */
    hasEventAccess(): boolean {
      const entries =
        this.user?.permissions?.["partnerPortal:useEventDashboard"];
      if (!entries) return false;
      return entries.some((e) => e.startsWith("venue:"));
    },
  },
});
